import { call, put, select, delay } from 'redux-saga/effects';
import { replaceAuth } from '../Redux/AuthActions';
import { reloadApp, setSagaMessage } from '../Redux/ApplicationActions';
import { triggerChatSocket } from '../Redux/ChatListActions';
import storage from '../Utils/storage';
import type { AuthState, RegistrationState, UpdateProfileData, UserProfile } from 'flowhawk-core';
import { setCheckRegistrationStatus } from 'flowhawk-core';
import type { RootState } from '../Redux';
import type { SagaIterator } from 'redux-saga';
import { apiPost } from './ApiSaga';
import api from '../Services/Api';
import { call as typedCall, select as typedSelect } from 'typed-redux-saga';
import type { ChatSocketStatus } from '../Utils/types';

const AUTH_KEY = 'auth';

export function* loadAuthSaga(): SagaIterator<void> {
  // Check to see if auth is already in the redux store
  const auth: AuthState = yield select((state: RootState) => state.auth);

  // don't do anything if already authenticated
  if (auth && auth.authenticated) {
    return;
  }

  const storedAuth: AuthState = yield call(getFromStorage, AUTH_KEY);

  // replace the 'auth' slice in the redux store
  // with that loaded from storage
  if (storedAuth) {
    yield put(replaceAuth(storedAuth));
  }
}

export function* persistAuthSaga(): SagaIterator<void> {
  // Ensure auth reducer has updated state before we try to persist it
  yield delay(50);

  const auth: AuthState = yield select((state: RootState) => state.auth);

  if (auth) {
    yield call(saveToStorageSaga, AUTH_KEY, auth);
  }
}

export function* setCheckRegistrationStatusSaga(): SagaIterator<void> {
  const { checkRegistrationStatus }: RegistrationState = yield select((state: RootState) => state.registration);
  if (!checkRegistrationStatus) {
    yield put(setCheckRegistrationStatus(true));
  }
}

export function* refreshSuccessSaga(): SagaIterator<void> {
  yield put(triggerChatSocket());
}

export function* deleteAuthSaga(): SagaIterator<void> {
  yield call([storage, 'removeItem'], AUTH_KEY);

  // Reload the app (go to login screen) as we delete auth from
  // state when auth sessions are terminated and we listen for
  // actions like LOGOUT_SUCCESS, TOKEN_EXPIRED, and FORCE_RELOGIN
  yield put(reloadApp());
}

// Reconnects the chat socket when the user's access level in their profile is updated
// This is intended for when a user requests to join an organization and is awaiting approval
// Upon approval, the user's access level will change and we need to reconnect the chat socket
// to ensure they have the correct permissions
export function* reconnectSocketOnProfileUpdateSaga({ user }: UpdateProfileData): SagaIterator<void> {
  // Check if user is authenticated and return early if not–this can occur when first logging in
  const authenticated: boolean = yield select((state: RootState) => state.auth.authenticated);
  if (!authenticated) {
    return;
  }

  // Get existing auth profile state to check for a change to access level
  const currentProfile: UserProfile = yield select((state: RootState) => state.auth.profile);
  const socketStatus: ChatSocketStatus = yield select((state: RootState) => state.chatList.socketStatus);
  const socketNotConnectedOrConnecting = socketStatus !== 'connected' && socketStatus !== 'connecting';

  // Reconnect if there wasn't previously a profile or if the access level changed
  // or if the socket is not connected or connecting
  if (!currentProfile || (user && user.accessLevel !== currentProfile.accessLevel) || socketNotConnectedOrConnecting) {
    yield put(triggerChatSocket());
  }
}

function* getFromStorage(key: string): SagaIterator<AuthState | null> {
  try {
    const data = yield call([storage, 'getItem'], key);
    return JSON.parse(data);
  } catch (error) {
    console.error('Failed to read auth state from storage:', error);
  }

  return null;
}

function* saveToStorageSaga(key: string, data: AuthState): SagaIterator<void> {
  try {
    yield call([storage, 'setItem'], key, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save auth state to storage:', error);
  }
}

export function* redirectToAdminPortalSaga(): SagaIterator<void> {
  const auth = yield* typedSelect((state: RootState) => state.auth);

  const { result, error } = yield* typedCall(
    apiPost<{ reroute_token: string; user_id: number }>,
    api.txp.redirectToAdminPortal,
    undefined,
    {
      access: auth.accessToken,
      refresh: auth.refreshToken,
    },
  );

  if (error) {
    yield put(setSagaMessage('', 'Failed to redirect to admin portal', ''));
    console.error('Failed to redirect to admin portal:', error);
    return;
  }

  if (result?.reroute_token && result?.user_id) {
    const adminPortalUrl = import.meta.env.VITE_ADMIN_PORTAL_URL || '';
    const redirectUrl = `${adminPortalUrl}?reroute_token=${result.reroute_token}&user_id=${result.user_id}`;
    window.open(redirectUrl, '_blank');
  }
}
